import type { ClientOp } from "../../shared/protocol.js";
import type { Course, CourseMap, CourseMapSummary, Template } from "../../shared/types.js";
import type { Role } from "./storage.js";

export class ApiError extends Error {
  status: number;
  body: Record<string, unknown>;
  constructor(message: string, status: number, body: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/**
 * A fetch that never reached the server at all — offline, a dropped connection, a
 * content blocker, a request that timed out. The browser gives this almost no text to
 * work with ("Load failed" in Safari, "Failed to fetch" in Chrome), which reads to
 * anyone looking at it as if the app rejected what they typed. It didn't: nothing got
 * an answer. Status 0 so callers can tell it apart from a real HTTP status.
 */
export function isNetworkError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 0;
}

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    });
  } catch (cause) {
    throw new ApiError(
      `Couldn't reach the server (${path}) — the request didn't complete. Check the connection and try again.`,
      0,
      { path, cause: String(cause) },
    );
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; [k: string]: unknown };
    throw new ApiError(body.error ?? `Request failed (${res.status})`, res.status, body);
  }
  return res;
}

export async function createTournament(name: string): Promise<{ code: string; adminToken: string }> {
  const res = await apiFetch("/tournament", { method: "POST", body: JSON.stringify({ name }) });
  return res.json();
}

export interface PublicRoster {
  code: string;
  name: string;
  status: string;
  players: { id: string; name: string; claimed: boolean }[];
  spectatorsEnabled: boolean;
  expiresAt: number;
}

export async function getPublicRoster(code: string): Promise<PublicRoster> {
  const res = await apiFetch(`/tournament/${encodeURIComponent(code)}`);
  return res.json();
}

export async function claimPlayer(
  code: string,
  playerId: string,
  confirm = false,
): Promise<{ token: string }> {
  const res = await apiFetch(`/tournament/${encodeURIComponent(code)}/claim`, {
    method: "POST",
    body: JSON.stringify({ playerId, confirm }),
  });
  return res.json();
}

/** The read-only way in. Everyone watching a given game shares one token, so there
 * is no name to pick and nothing to reclaim off anybody. */
export async function spectate(code: string): Promise<{ token: string; name: string }> {
  const res = await apiFetch(`/tournament/${encodeURIComponent(code)}/spectate`, {
    method: "POST",
  });
  return res.json();
}

/** Checks a token before anything is written to this device's storage. The token goes
 * in the body rather than the path, like `adminOp`'s, so it stays out of access logs. */
export async function verifyToken(
  code: string,
  token: string,
): Promise<{ role: Role; code: string; name: string }> {
  const res = await apiFetch(`/tournament/${encodeURIComponent(code)}/verify`, {
    method: "POST",
    body: JSON.stringify({ token }),
  });
  return res.json();
}

export async function adminOp(code: string, adminToken: string, op: ClientOp): Promise<void> {
  await apiFetch(`/tournament/${encodeURIComponent(code)}/admin/op`, {
    method: "POST",
    body: JSON.stringify({ adminToken, clientOpId: crypto.randomUUID(), op }),
  });
}

// Both exports go through apiFetch for its status check. Calling `fetch` directly meant
// a 404 or a 500 was never noticed: the JSON download handed the user a file containing
// `{"error":"not found"}` under the tournament's own name, and the text export copied
// the error body to the clipboard and reported it as "Copied to clipboard."
export async function exportTournamentJson(code: string): Promise<unknown> {
  const res = await apiFetch(`/tournament/${encodeURIComponent(code)}/export?format=json`);
  return res.json();
}

export async function exportTournamentText(code: string): Promise<string> {
  const res = await apiFetch(`/tournament/${encodeURIComponent(code)}/export?format=text`);
  return res.text();
}

/** The one header the admin key ever travels in. Declared up here because course
 * search, import and every library write now carry it too. */
function adminHeaders(key: string): HeadersInit {
  return { "x-admin-key": key };
}

export interface CourseSearchResult {
  id: string;
  /** Club and course together, without saying the same thing twice. */
  name: string;
  clubName: string | null;
  courseName: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  /** Tee boxes on the course. Search reports a count, not the tee data itself. */
  teeCount: number | null;
}

/** Where the deployment's shared golfcourseapi.com allowance stands today. */
export interface CourseApiBudget {
  day: string;
  used: number;
  limit: number;
  remaining: number;
}

/**
 * Course search and import are admin-only, and every call to them spends one of the
 * deployment's fifty daily golfcourseapi.com requests unless it comes back `cached`.
 * Both facts are why the key is a parameter here rather than something this module
 * reaches for: the caller has already unlocked, and knows it.
 */
export async function searchExternalCourses(
  key: string,
  q: string,
  refresh = false,
): Promise<{ results: CourseSearchResult[]; cached: boolean; budget: CourseApiBudget }> {
  const res = await apiFetch(
    `/courses/search?q=${encodeURIComponent(q)}${refresh ? "&refresh=1" : ""}`,
    { headers: adminHeaders(key) },
  );
  return (await res.json()) as {
    results: CourseSearchResult[];
    cached: boolean;
    budget: CourseApiBudget;
  };
}

export async function importExternalCourse(
  key: string,
  externalId: string,
  refresh = false,
): Promise<{
  course: Course;
  warnings: string[];
  cached: boolean;
  budget: CourseApiBudget;
}> {
  const res = await apiFetch(
    `/courses/import?externalId=${encodeURIComponent(externalId)}${refresh ? "&refresh=1" : ""}`,
    { headers: adminHeaders(key) },
  );
  return (await res.json()) as {
    course: Course;
    warnings: string[];
    cached: boolean;
    budget: CourseApiBudget;
  };
}

/** Today's allowance, without spending any of it. */
export async function courseApiBudget(key: string): Promise<CourseApiBudget> {
  const res = await apiFetch("/courses/budget", { headers: adminHeaders(key) });
  return res.json();
}

export async function listLibraryCourses(): Promise<Course[]> {
  const res = await apiFetch("/library/courses");
  return res.json();
}

export async function getLibraryCourse(id: string): Promise<Course> {
  const res = await apiFetch(`/library/courses/${encodeURIComponent(id)}`);
  return res.json();
}

/** Creates a course, or overwrites one when `course.id` is set — the library's POST is
 * an upsert. Admin-only: one course row is read by every tournament that plays it. */
export async function saveLibraryCourse(
  key: string,
  course: Partial<Course>,
): Promise<{ course: Course; warnings: string[] }> {
  const res = await apiFetch("/library/courses", {
    method: "POST",
    headers: adminHeaders(key),
    body: JSON.stringify(course),
  });
  return res.json();
}

/**
 * Every course map's identity and completeness, without the geometry.
 *
 * Small enough to fetch up front; the geometry itself is a separate, much bigger read
 * that only happens for a course actually being played.
 */
export async function listCourseMaps(): Promise<CourseMapSummary[]> {
  const res = await apiFetch("/maps");
  const body = (await res.json()) as { maps: CourseMapSummary[] };
  return body.maps;
}

/** One map's geometry. `sha` comes from the manifest and makes the response cacheable. */
export async function getCourseMap(
  mapId: string,
  sha?: string,
): Promise<{ map: CourseMap; sha: string }> {
  const query = sha ? `?sha=${encodeURIComponent(sha)}` : "";
  const res = await apiFetch(`/maps/${encodeURIComponent(mapId)}${query}`);
  return (await res.json()) as { map: CourseMap; sha: string };
}

export async function updateLibraryCourse(
  key: string,
  id: string,
  patch: Partial<Course>,
): Promise<{ course: Course; warnings: string[] }> {
  const res = await apiFetch(`/library/courses/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: adminHeaders(key),
    body: JSON.stringify(patch),
  });
  return res.json();
}

export async function listTemplates(): Promise<Template[]> {
  const res = await apiFetch("/library/templates");
  return res.json();
}

export async function saveTemplate(template: Partial<Template>): Promise<Template> {
  const res = await apiFetch("/library/templates", { method: "POST", body: JSON.stringify(template) });
  return res.json();
}

export async function cloneTemplate(id: string): Promise<{ code: string; adminToken: string }> {
  const res = await apiFetch(`/templates/${encodeURIComponent(id)}/clone`, { method: "POST" });
  return res.json();
}

export async function deleteOwnTournament(code: string, adminToken: string): Promise<void> {
  await apiFetch(`/tournament/${encodeURIComponent(code)}/delete`, {
    method: "POST",
    body: JSON.stringify({ adminToken }),
  });
}

/**
 * The control in the experiment: a request that needs no key and touches no storage.
 * If this one answers, the app can reach the server and a failure elsewhere is about
 * that request, not the connection. See worker/index.ts's /api/health.
 */
export async function health(): Promise<{ ok: boolean; time: number; build: string }> {
  const res = await apiFetch("/health");
  return res.json();
}

// ---------- admin area ----------

export interface AdminTournament {
  code: string;
  name: string;
  status?: string;
  createdAt: number;
  lastActivityAt?: number;
  expiresAt?: number;
  playerCount?: number;
  roundCount?: number;
  scoreCount?: number;
  storageBytes?: number;
  gone?: boolean;
  /** Its Durable Object threw when asked for a summary — see worker/routes/admin.ts. */
  unreachable?: boolean;
}

export interface AdminUsage {
  today: string;
  requestsToday: number;
  wsMessagesToday: number;
  billedRequestsToday: number;
  /** Measured by the database from the same per-query counts Cloudflare bills on. */
  rowsReadToday: number;
  rowsWrittenToday: number;
  storageBytes: number;
  tournamentCount: number;
  /** How many of `tournamentCount` the figures actually cover: one Worker request can
   * only read so many Durable Objects. `truncated` says when that bit. */
  countedTournaments?: number;
  truncated?: boolean;
  limits: {
    doRequestsPerDay: number;
    rowsReadPerDay: number;
    rowsWrittenPerDay: number;
    storageBytes: number;
    wsMessagesPerRequest: number;
  };
  /** golfcourseapi.com's daily allowance, for course lookups. */
  courseApi: CourseApiBudget;
  percentOfRequestLimit: number;
  percentOfReadLimit: number;
  percentOfWriteLimit: number;
  percentOfStorageLimit: number;
  history: {
    day: string;
    requests: number;
    wsMessages: number;
    billed: number;
    /** Null for a day from before rows were measured. */
    rowsRead: number | null;
    rowsWritten: number | null;
  }[];
}

export async function checkAdminKey(key: string): Promise<void> {
  await apiFetch("/admin/check", { headers: adminHeaders(key) });
}

export async function adminListTournaments(
  key: string,
): Promise<{ tournaments: AdminTournament[]; total: number; truncated: boolean }> {
  const res = await apiFetch("/admin/tournaments", { headers: adminHeaders(key) });
  const body = (await res.json()) as {
    tournaments: AdminTournament[];
    total?: number;
    truncated?: boolean;
  };
  return {
    tournaments: body.tournaments,
    total: body.total ?? body.tournaments.length,
    truncated: Boolean(body.truncated),
  };
}

export async function adminUsage(key: string): Promise<AdminUsage> {
  const res = await apiFetch("/admin/usage", { headers: adminHeaders(key) });
  return res.json();
}

/** One real read against each external service the deployment depends on, run from
 * inside the Worker — see worker/routes/admin.ts's handleAdminDebug. The shape is
 * deliberately open: it is a report to be read, not data the app depends on. */
export async function adminDebugReport(key: string): Promise<Record<string, unknown>> {
  const res = await apiFetch("/admin/debug", { headers: adminHeaders(key) });
  return res.json();
}

export async function adminDeleteTournament(key: string, code: string): Promise<void> {
  await apiFetch(`/admin/tournaments/${encodeURIComponent(code)}`, {
    method: "DELETE",
    headers: adminHeaders(key),
  });
}

export async function adminOrganiserLink(
  key: string,
  code: string,
): Promise<{ code: string; name: string; adminToken: string }> {
  const res = await apiFetch(`/admin/tournaments/${code}/organizer-link`, {
    headers: adminHeaders(key),
  });
  return res.json();
}

export interface AdminCourse {
  id: string;
  name: string;
  source: string;
  verified: boolean;
  teeSetNames: string[];
  usedBy: { code: string; name: string }[];
}

export async function adminListCourses(key: string): Promise<AdminCourse[]> {
  const res = await apiFetch("/admin/courses", { headers: adminHeaders(key) });
  return ((await res.json()) as { courses: AdminCourse[] }).courses;
}

export async function adminDeleteCourse(key: string, id: string): Promise<void> {
  await apiFetch(`/admin/courses/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: adminHeaders(key),
  });
}
